import styled from '@emotion/styled';

import Button from 'design-system/Button';
import { Input } from 'design-system/Input';
import { authColors, authLayout, authRadius } from 'design-system/tokens';

export const Column = styled.div<{ gap?: number; width?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 32 }) => gap}px;
  width: ${({ width }) => width ?? authLayout.formWidth};

  @media (max-width: ${authLayout.mobileBreakpoint}) {
    width: 100%;
  }
`;

export const Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px dashed ${authColors.disabled};
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled.h1`
  margin: 0;
  font-family: 'Mangueira', sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  color: ${authColors.brand};
`;

export const Subtitle = styled.p`
  margin: 0;
  font-family: 'Mangueira', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: ${authColors.textBody};
`;

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${authLayout.fieldWidth};

  @media (max-width: ${authLayout.mobileBreakpoint}) {
    width: 100%;
  }
`;

export const TextInput = styled(Input)`
  width: 100%;
  gap: 8px;

  > *:first-child {
    margin-bottom: 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    color: ${authColors.textStrong};

    span {
      color: ${authColors.required};
    }
  }

  > *:last-child {
    height: 48px;
    padding: 12px 32px;
    border-color: ${({ error }) =>
      error ? authColors.required : authColors.textStrong};
    border-radius: ${authRadius.pill};

    &:focus-within {
      border-color: ${({ error }) =>
        error ? authColors.required : authColors.brand};
    }
  }

  input {
    line-height: 16px;
    color: ${authColors.textStrong};
    /* senha mascarada usa Regular; e-mail e senha exibida usam Medium */
    font-weight: ${({ type }) => (type === 'password' ? 400 : 500)};

    font-family: ${({ type }) =>
      type === 'password' ? 'Verdana, sans-serif' : "'Mangueira', sans-serif"};
    font-size: ${({ type }) => (type === 'password' ? '21px' : '12px')};
    letter-spacing: ${({ type }) =>
      type === 'password' ? '-0.7px' : 'normal'};

    &::placeholder {
      color: ${authColors.textMuted};
      font-family: 'Mangueira', sans-serif;
      font-size: 12px;
      font-weight: 400;
      letter-spacing: normal;
    }
  }
`;

export const ErrorText = styled.span`
  font-family: 'Mangueira', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: ${authColors.required};
`;

export const ErrorTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  box-sizing: border-box;
  gap: 4px;
  min-width: 128px;
  max-width: ${authLayout.fieldWidth};
  min-height: 28px;
  padding: 6px 12px;
  text-align: center;
  background: ${authColors.errorTagBg};
  border: 1px solid ${authColors.required};
  border-radius: ${authRadius.pill};
  font-family: 'Mangueira', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  color: ${authColors.errorTagText};

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`;

export const ToggleVisibility = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${authColors.textStrong};
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${authColors.brand};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: ${authLayout.fieldWidth};

  @media (max-width: ${authLayout.mobileBreakpoint}) {
    width: 100%;
  }
`;

export const PrimaryButton = styled(Button)`
  height: 48px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  background: ${authColors.brand};
  color: ${authColors.white};

  &:hover:not(:disabled) {
    background: ${authColors.brand};
    opacity: 0.9;
  }

  &:disabled {
    background: ${authColors.disabled};
    color: ${authColors.textMuted};
    opacity: 1;
  }
`;

export const OutlineButton = styled(Button)`
  height: 48px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  background: ${authColors.white};
  color: ${authColors.brand};
  border: 1px solid ${authColors.brand};
`;
